import { Alert, Card, Spin, Typography, message } from "antd";
import React, { useState } from "react";
import MainLayout from "../../components/layout/MainLayout";
import { useAuth } from "../../hooks/useAuth";
import { useOpportunities } from "../../hooks/useOpportunities";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useUserRoles } from "../../hooks/useUserRegionAndRole";
import TenderForm from "./form";
import { OpportunityRecord } from "./helper/opportunities";
import ImportOpportunity from "./helper/opportunities/ImportOpportunity";

const { Title, Text } = Typography;

const NewTender: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { user } = useAuth();

  // Fetch user role and region data (single API call)
  const { data: userRoleData, isLoading: isLoadingUserRole } = useUserRoles(
    user?.email,
  );
  const userData = userRoleData?.data;
  const role = !Array.isArray(userData) ? userData?.RoleDisplayName : undefined;
  const userRegion = !Array.isArray(userData) ? userData?.Region : undefined;
  const { data: opportunities, isLoading: opportunitiesLoading } =
    useOpportunities();

  // Check if all APIs are loaded completely
  const allApisLoaded =
    !opportunitiesLoading && !isLoadingUserRole;
  // Show spinner unless every API is loaded
  const isPageLoading = !allApisLoaded;

  const [isImportedData, setIsImportedData] = useState(false);
  const [importedOpportunity, setImportedOpportunity] =
    useState<OpportunityRecord | null>(null);
  const [selectedOpportunityForImport, setSelectedOpportunityForImport] =
    useState<OpportunityRecord | null>(null);

  const handleCreateNewTender = () => {
    setIsImportedData(false);
    setImportedOpportunity(null);
    message.info("Ready to create a new tender");
  };

  // Handle opportunity selection in modal (just selection, not import yet)
  const handleOpportunitySelect = (opportunity: OpportunityRecord | null) => {
    setSelectedOpportunityForImport(opportunity);
  };

  // Handle import confirmation when "Import Client Details" is clicked
  const handleImportConfirm = async (opportunity: OpportunityRecord) => {
    try {
      // Set imported data states
      setIsImportedData(true);
      setImportedOpportunity(opportunity);
      setSelectedOpportunityForImport(null); // Reset selection
    } catch (error) {
      message.error({
        content: "Failed to import opportunity",
        key: "loadTender",
        duration: 3,
      });
    }
  };

  return (
    <MainLayout canProceed={false} showFooter={false}>
      <div className="max-w-4xl mx-auto">
        {!isPageLoading && role !== "Estimator" && (
          <Alert
            type="info"
            showIcon
            message="Authorized users have the ability to create tenders, while submitting and approving them is governed by role-based permissions assigned to Estimators, Senior Estimators, and the Commercial Director."
            className="mb-4"
          />
        )}
        <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <Title
              level={3}
              className="mb-1"
              style={{ fontWeight: 600, fontSize: "20px", color: "#111827" }}
            >
              Create new tender
            </Title>
            <Text style={{ color: "#9ca3af", fontSize: "14px" }}>
              Create or import tender details for your project
            </Text>
          </div>

          <div className="px-6 py-6">
            {/* Single loader for both sections */}
            {isPageLoading ? (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <Spin size="large" tip="Loading..." />
              </div>
            ) : (
              <>
                {/* Import Section */}
                <ImportOpportunity
                  opportunities={opportunities || []}
                  selectedOpportunity={selectedOpportunityForImport}
                  onOpportunitySelect={handleOpportunitySelect}
                  onImportConfirm={handleImportConfirm}
                />

                {/* Tender Form - handles both regular and imported data display */}
                <TenderForm
                  userRoleData={userRoleData}
                  isImportedData={isImportedData}
                  importedOpportunity={importedOpportunity || undefined}
                  onCreateNew={handleCreateNewTender}

                />
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default NewTender;
